import { useState, useEffect, lazy, Suspense } from 'react';
import { forceScrollToTop } from './hooks/useScrollToTop';
import Header from './components/Header';
import Home from './components/Home';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import ScrollToTopButton from './components/ScrollToTopButton';
import { PWAProvider } from './context/PWAContext';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import './App.css';

// Load Firebase diagnostics utility (available in browser console)
import './utils/firebase-diagnostics';

// Lazy load all page components for better performance
const About = lazy(() => import('./pages/About'));
const CTA = lazy(() => import('./components/CTA'));
const Companies = lazy(() => import('./pages/Companies'));
const POPIPolicy = lazy(() => import('./pages/POPIPolicy'));
const EthicsCommittees = lazy(() => import('./pages/EthicsCommittees'));
const RFPSubmission = lazy(() => import('./pages/RFPSubmission'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const PostJob = lazy(() => import('./pages/PostJob'));
const ConsultantDirectory = lazy(() => import('./pages/ConsultantDirectory'));
const Community = lazy(() => import('./pages/Community'));
const SitesDirectory = lazy(() => import('./pages/SitesDirectory'));
const Chat = lazy(() => import('./pages/Chat'));
const Connections = lazy(() => import('./pages/Connections'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ConsultantRegistration = lazy(() => import('./pages/ConsultantRegistration'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConsultants = lazy(() => import('./pages/TermsConsultants'));
const TermsCompanies = lazy(() => import('./pages/TermsCompanies'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines'));
const MedicalDisclaimer = lazy(() => import('./pages/MedicalDisclaimer'));
const DownloadDocuments = lazy(() => import('./pages/DownloadDocuments'));

function App() {
  type PageType =
    | 'home'
    | 'about'
    | 'contact'
    | 'consultants'
    | 'companies'
    | 'popi-policy'
    | 'privacy-policy'
    | 'terms-of-service'
    | 'terms-consultants'
    | 'terms-companies'
    | 'community-guidelines'
    | 'medical-disclaimer'
    | 'download-documents'
    | 'ethics-committees'
    | 'rfp-submission'
    | 'analytics'
    | 'subscriptions'
    | 'profile'
    | 'login'
    | 'marketplace'
    | 'chat'
    | 'connections'
    | 'notifications'
    | 'register'
    | 'consultant-register'
    | 'forgot-password'
    | 'community'
    | 'sites'
    | 'post-job';

  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    role: string;
    name?: string;
  } | null>(null);

  // Helper to navigate to a page and update the URL
  const navigateToPage = (page: PageType) => {
    // CRITICAL: Force scroll to top BEFORE any state changes
    // This must complete synchronously
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }

    // Use setTimeout to ensure scroll completes before state update
    setTimeout(() => {
      setCurrentPage(page);

      // Update URL without reloading the page
      const newUrl =
        page === 'home'
          ? `${window.location.pathname}`
          : `${window.location.pathname}?page=${page}`;
      window.history.pushState({ page }, '', newUrl);

      // Force scroll again after state change
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
  };

  useEffect(() => {
    // Disable browser's scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Force initial scroll to top
    forceScrollToTop();

    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('userId');
    const userRole = localStorage.getItem('userRole');
    const userName = localStorage.getItem('userName');

    if (token && userId && userRole) {
      setIsLoggedIn(true);
      setCurrentUser({ id: userId, role: userRole, name: userName || undefined });
    }

    // Check URL query parameters for page navigation
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get('page');
    if (pageParam) {
      setCurrentPage(pageParam as PageType);
    }

    // Handle browser back/forward buttons
    const handlePopState = () => {
      // CRITICAL: Synchronous scroll to top FIRST
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
      }

      const params = new URLSearchParams(window.location.search);
      const pageParam = params.get('page');

      // Use setTimeout to ensure scroll completes
      setTimeout(() => {
        if (pageParam) {
          setCurrentPage(pageParam as PageType);
        } else {
          setCurrentPage('home');
        }
        // Force scroll again after state change
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const activeGoogleFlow = sessionStorage.getItem('googleAuthFlow');
    if (activeGoogleFlow === 'login') {
      setCurrentPage('login');
    }
  }, []);

  // Always scroll to top on page change - aggressive enforcement
  useEffect(() => {
    // Multiple immediate scroll attempts
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }

    // Keep forcing scroll for a short period
    const interval = setInterval(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 10);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 100);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [currentPage]);

  const handleLoginSuccess = (userId: string, role: string, userName?: string) => {
    setIsLoggedIn(true);
    setCurrentUser({ id: userId, role, name: userName });
    if (userName) {
      localStorage.setItem('userName', userName);
    }
    setCurrentPage('home');
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    setIsLoggedIn(false);
    setCurrentUser(null);
    setCurrentPage('home');
  };

  const handleRegisterSuccess = () => {
    setCurrentPage('login');
  };
  const [selectedService, setSelectedService] = useState<string>('');

  const handlePageChange = (page: string, serviceTitle?: string) => {
    // CRITICAL: Immediate synchronous scroll to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }

    // Use setTimeout to ensure scroll completes before state update
    setTimeout(() => {
      // Use View Transitions API if available
      if (document.startViewTransition) {
        document.startViewTransition(() => {
          setCurrentPage(page as PageType);
          if (serviceTitle) {
            setSelectedService(serviceTitle);
          }
          // Force scroll again inside transition
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        });
      } else {
        // Fallback for browsers without View Transitions API
        setCurrentPage(page as PageType);
        if (serviceTitle) {
          setSelectedService(serviceTitle);
        }
        // Force scroll after state change
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    }, 0);
  };

  return (
    <PWAProvider>
      <div className="app">
        <OfflineIndicator />
        <Header
          currentPage={currentPage}
          setCurrentPage={navigateToPage}
          isLoggedIn={isLoggedIn}
          currentUser={currentUser}
          onLogout={handleLogout}
        />
        <CookieConsent setCurrentPage={navigateToPage} />

        <Suspense
          fallback={
            <div
              className="loading-container"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
              }}
            >
              <div className="loader-spinner"></div>
            </div>
          }
        >
          {currentPage === 'home' && (
            <Home
              onNavigate={handlePageChange}
              isLoggedIn={isLoggedIn}
              userRole={currentUser?.role}
            />
          )}
          {currentPage === 'about' && <About />}
          {currentPage === 'contact' && <CTA fullPage={true} setCurrentPage={navigateToPage} />}
          {currentPage === 'companies' && (
            <Companies
              isLoggedIn={isLoggedIn}
              currentUserRole={currentUser?.role}
              onNavigate={handlePageChange}
            />
          )}
          {currentPage === 'popi-policy' && <POPIPolicy setCurrentPage={navigateToPage} />}
          {currentPage === 'privacy-policy' && <PrivacyPolicy isLoggedIn={isLoggedIn} />}
          {currentPage === 'terms-of-service' && (
            <TermsOfService userRole={currentUser?.role} setCurrentPage={navigateToPage} />
          )}
          {currentPage === 'terms-consultants' && <TermsConsultants userRole={currentUser?.role} />}
          {currentPage === 'terms-companies' && <TermsCompanies userRole={currentUser?.role} />}
          {currentPage === 'community-guidelines' && <CommunityGuidelines />}
          {currentPage === 'medical-disclaimer' && <MedicalDisclaimer />}
          {currentPage === 'download-documents' && (
            <DownloadDocuments isLoggedIn={isLoggedIn} userRole={currentUser?.role} />
          )}
          {currentPage === 'ethics-committees' && (
            <EthicsCommittees setCurrentPage={navigateToPage} />
          )}
          {currentPage === 'rfp-submission' && (
            <RFPSubmission serviceTitle={selectedService} setCurrentPage={navigateToPage} />
          )}
          {currentPage === 'analytics' && <Analytics />}
          {currentPage === 'subscriptions' && (
            <Subscriptions
              currentUserId={currentUser?.id}
              currentUserType={currentUser?.role as 'professional' | 'company' | 'team' | undefined}
              onNavigateToLogin={() => navigateToPage('login')}
            />
          )}
          {currentPage === 'login' && (
            <Login
              onLoginSuccess={handleLoginSuccess}
              onSwitchToRegister={() => navigateToPage('register')}
              onSwitchToForgotPassword={() => navigateToPage('forgot-password')}
            />
          )}
          {currentPage === 'register' && !isLoggedIn && (
            <Register
              onRegisterSuccess={handleRegisterSuccess}
              onSwitchToLogin={() => navigateToPage('login')}
            />
          )}
          {currentPage === 'consultant-register' && !isLoggedIn && (
            <ConsultantRegistration
              onSuccess={handleRegisterSuccess}
              onBack={() => navigateToPage('register')}
              referrerId={null}
            />
          )}
          {currentPage === 'forgot-password' && !isLoggedIn && (
            <ForgotPassword onBackToLogin={() => navigateToPage('login')} />
          )}
          {currentPage === 'profile' && isLoggedIn && currentUser && (
            <Profile userId={currentUser.id} userRole={currentUser.role} />
          )}

          {/* NEW MARKETPLACE PAGES */}
          {currentPage === 'marketplace' && (
            <Marketplace
              userRole={currentUser?.role || 'guest'}
              userId={currentUser?.id}
              setCurrentPage={(page: string) => navigateToPage(page as PageType)}
            />
          )}
          {currentPage === 'post-job' && isLoggedIn && currentUser?.role === 'company' && (
            <PostJob
              userId={currentUser?.id}
              companyId={currentUser?.id}
              companyName={currentUser?.name}
              onBackToMarketplace={() => navigateToPage('marketplace')}
            />
          )}
          {currentPage === 'consultants' && (
            <ConsultantDirectory
              userId={currentUser?.id}
              userRole={currentUser?.role}
              setCurrentPage={(page: string) => navigateToPage(page as PageType)}
            />
          )}
          {currentPage === 'community' && (
            <Community
              userId={currentUser?.id}
              userName={currentUser?.name || 'Anonymous User'}
              userRole={currentUser?.role}
              setCurrentPage={(page: string) => navigateToPage(page as PageType)}
            />
          )}
          {currentPage === 'sites' && <SitesDirectory userId={currentUser?.id} />}
          {currentPage === 'chat' && isLoggedIn && <Chat />}
          {currentPage === 'connections' && isLoggedIn && currentUser && (
            <Connections userId={currentUser.id} />
          )}
          {currentPage === 'notifications' && isLoggedIn && currentUser && (
            <Notifications
              userId={currentUser.id}
              setCurrentPage={(page: string) => navigateToPage(page as PageType)}
            />
          )}
        </Suspense>

        {currentPage !== 'login' && currentPage !== 'register' && currentPage !== 'profile' && (
          <Footer
            setCurrentPage={navigateToPage}
            isLoggedIn={isLoggedIn}
            userRole={currentUser?.role}
          />
        )}
        <ScrollToTopButton />
        <PWAInstallPrompt />
      </div>
    </PWAProvider>
  );
}

export default App;
